/**
 * Confirms removal of a server-proven legacy setup placeholder.
 *
 * This action is intentionally distinct from grant revocation and resource
 * disconnection: it removes one untouched local setup tuple and never calls
 * Google. The parent owns the mutation so the dialog stays a small,
 * controlled presentation component with pending and exit-window guards.
 */

import { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "motion/react";
import { Trash } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";

export interface DiscardIncompleteSetupTarget {
  integrationId: string;
  clientId: string;
  platform: string;
  displayName: string;
  oauthTokenId: string;
  oauthGrantResourceId: string;
}

interface DiscardIncompleteSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DiscardIncompleteSetupTarget;
  isPending: boolean;
  onConfirm: () => void;
}

export function DiscardIncompleteSetupDialog({
  open,
  onOpenChange,
  target,
  isPending,
  onConfirm,
}: DiscardIncompleteSetupDialogProps) {
  const openRef = useRef(open);
  const pendingRef = useRef(isPending);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    pendingRef.current = isPending;
  }, [isPending]);

  function handleOpenChange(next: boolean): void {
    if (!next && pendingRef.current) return;
    onOpenChange(next);
  }

  function handleConfirm(): void {
    if (!openRef.current || pendingRef.current || isPending) return;
    onConfirm();
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-semibold leading-none tracking-tight">
                    <Trash size={19} className="shrink-0 text-destructive" />
                    Remove incomplete setup?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    Remove the unfinished {target.displayName} setup from this
                    client.
                  </DialogPrimitive.Description>
                </div>

                <div className="mt-6 space-y-4">
                  <motion.p
                    className="text-sm text-foreground"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    This removes only this incomplete setup from ProvenLeap. It
                    does not revoke Google access and will not affect Search
                    Console or any other Google connections.
                  </motion.p>

                  <motion.p
                    className="text-[13px] text-muted-foreground"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    You can connect {target.displayName} again and select the
                    correct property afterwards.
                  </motion.p>

                  <motion.div
                    className="flex justify-end gap-3"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-2xl bg-background text-[13px] shadow-xs cursor-pointer hover:-translate-y-0.5 hover:bg-background hover:shadow-sm transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      onClick={() => handleOpenChange(false)}
                      disabled={isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      className="rounded-2xl text-[13px] cursor-pointer"
                      onClick={handleConfirm}
                      disabled={isPending}
                    >
                      <Trash size={16} className="mr-1.5" />
                      {isPending ? "Removing…" : "Remove setup"}
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
